import { useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { Center, Loader } from "@mantine/core";

import { api } from "./api/client";
import type { CurrentUser } from "./api/types";
import { AppLayout } from "./layouts/AppLayout";
import { Login } from "./pages/Login";
import { Dashboard } from "./pages/Dashboard";
import { Customers } from "./pages/Customers";
import { Batches } from "./pages/Batches";
import { BatchDetail } from "./pages/BatchDetail";
import { AuditLogPage } from "./pages/AuditLogPage";
import { BatchFlash } from "./pages/BatchFlash";
import { Documents } from "./pages/Documents";
import { Rma } from "./pages/Rma";
import { Shipments } from "./pages/Shipments";
import { ShipmentByCode } from "./pages/ShipmentByCode";
import { ShipmentDetail } from "./pages/ShipmentDetail";
import { Scanner } from "./pages/Scanner";
import { MobileStation } from "./pages/MobileStation";
import { DeviceLookup } from "./pages/DeviceLookup";
import { Godex } from "./pages/Godex";
import { LabelEditor } from "./pages/LabelEditor";
import { Drive } from "./pages/Drive";
import { Settings } from "./pages/Settings";
import { ShipmentStats } from "./pages/ShipmentStats";
import { Workers } from "./pages/Workers";
import { Procurement } from "./pages/Procurement";
import { ProcurementDetail } from "./pages/ProcurementDetail";
import { NotificationsPage } from "./pages/NotificationsPage";
import Stock from "./pages/Stock";
import FreeIssues from "./pages/FreeIssues";
import PublicSearch from "./pages/PublicSearch";
import { BrandingProvider } from "./contexts/BrandingContext";

export default function App() {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .me()
      .then((u) => setUser(u))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Center h="100vh">
        <Loader />
      </Center>
    );
  }

  return (
    <BrandingProvider>
      {!user ? (
        <Routes>
          <Route path="/s/:token" element={<PublicSearch />} />
          <Route path="/login" element={<Login onLogin={setUser} />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      ) : (
        <Routes>
          <Route path="/login" element={<Navigate to="/dashboard" replace />} />
          <Route path="/s/:token" element={<PublicSearch />} />
          <Route element={<AppLayout user={user} onLogout={() => setUser(null)} />}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/customers" element={<Customers />} />
            <Route path="/batches" element={<Batches />} />
            <Route path="/batches/:id" element={<BatchDetail />} />
            <Route path="/batches/:id/flash" element={<BatchFlash />} />
            <Route path="/shipments" element={<Shipments />} />
            <Route path="/shipments/:id" element={<ShipmentDetail />} />
            <Route path="/stock" element={<Stock />} />
            <Route path="/free-issues" element={<FreeIssues />} />
            <Route path="/sh/:code" element={<ShipmentByCode />} />
            <Route path="/scanner" element={<Scanner />} />
            <Route path="/statie" element={<MobileStation />} />
            <Route path="/device-lookup" element={<DeviceLookup />} />
            <Route path="/godex" element={<Godex />} />
            <Route path="/label-editor" element={<LabelEditor />} />
            <Route path="/documents" element={<Documents />} />
            <Route path="/rma" element={<Rma />} />
            <Route path="/workers" element={<Workers />} />
            <Route path="/settings" element={<Settings user={user} onUserUpdate={setUser} />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/shipment-stats" element={<ShipmentStats />} />
            {user.role === "admin" && <Route path="/drive" element={<Drive />} />}
            {user.role === "admin" && <Route path="/audit" element={<AuditLogPage />} />}
            <Route path="/procurement" element={<Procurement />} />
            <Route path="/procurement/:id" element={<ProcurementDetail />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Route>
        </Routes>
      )}
    </BrandingProvider>
  );
}
